/**
 * Trust funding: per-matter balances, reconciliation, and depletion risk
 * (KAN-41 / KAN-59 / KAN-85 / KAN-86).
 *
 * Trust is IOLTA money. A balance shown wrongly here is not a rendering bug —
 * somebody bills against funds that are not there, and that is a bar complaint.
 * Three rules follow from that and every function below is arranged around
 * them:
 *
 *  1. **A matter's balance stands alone.** Nothing nets one matter's trust
 *     against another's, at any level of aggregation. The firm-wide figures in
 *     `TrustFundingSummary` are deliberately two numbers that are never added.
 *  2. **A negative balance is an alarm.** It is either a real overdraw or a
 *     sync fault, and both need somebody today. It is its own risk level rather
 *     than the bottom of a scale.
 *  3. **Nothing is guessed.** No burn rate without history, no runway without a
 *     burn rate, no hearing cost the firm has not given us. Where a figure
 *     cannot be produced honestly the status says so, and the screen prints
 *     the status instead of a number.
 *
 * As with the AR risk rule, the thresholds travel with the request rather than
 * living in a table — see `architecture.md` §7. That is what keeps this a pure
 * read, keeps `trust_ledger.view` from needing a write grant beside it, and
 * makes a flagged matter reproducible from a link. KAN-81 will give Finance a
 * stored place to keep its preferred values; this module is the shape those
 * values will have.
 */
/**
 * The deterministic order a running balance is accumulated in.
 *
 * Same-day movements are the reason this is stated rather than left to the
 * query: two deposits and a withdrawal on one date can be replayed in six
 * orders, five of which produce a running balance history nobody would
 * recognise. Value date, then the moment the row was written here, then the id
 * — the last is arbitrary but total, which is the property that matters.
 */
export const TRUST_LEDGER_ORDER = 'occurred_at, created_at, id';
/* ------------------------------------------------------------------------ *
 * Reconciliation against Clio (KAN-85)
 * ------------------------------------------------------------------------ */
/**
 * How far our computed balance may sit from Clio's and still agree.
 *
 * Zero, and for a stronger reason than AR's zero tolerance. Both sides are
 * integer minor units, so there is no rounding for a tolerance to absorb — and
 * KAN-85's definition of done says discrepancies must be zero rather than
 * "close enough". A cent of slack here is a cent of client money nobody can
 * account for.
 */
export const TRUST_RECONCILIATION_TOLERANCE_MINOR = 0;
/**
 * Outcome of comparing one matter's computed balance against Clio's.
 *
 * `unavailable` is a first-class outcome, exactly as it is for the QuickBooks
 * AR reconciliation: Clio reports a trust balance per matter in
 * `matters.account_balances[]`, and a tenant, scope or matter that does not
 * carry one leaves us with nothing to compare against. Saying so beats
 * reporting a variance against an assumed zero, which would flag every matter
 * in the firm the first time the field was missing.
 */
export const TRUST_RECONCILIATION_STATUSES = ['reconciled', 'variance', 'unavailable'];
export const TRUST_RECONCILIATION_STATUS_META = {
    reconciled: {
        label: 'Agrees with Clio',
        description: "Our computed balance and Clio's own figure are identical to the cent.",
    },
    variance: {
        label: 'Disagrees with Clio',
        description: 'The two systems report different balances for this matter. Neither is preferred: both figures are shown, and the difference needs explaining before either is relied on.',
    },
    unavailable: {
        label: 'Not compared',
        description: 'Clio reported no trust balance for this matter, so there was nothing to compare against. Reported as uncompared rather than as agreement.',
    },
};
/* ------------------------------------------------------------------------ *
 * Burn rate and the depletion projection (KAN-86)
 * ------------------------------------------------------------------------ */
/**
 * How far back the burn rate is measured.
 *
 * Three months rather than the most recent one, because legal matter activity
 * is lumpy: a quiet month before a trial would produce a runway of years for
 * the matter about to consume its whole retainer. KAN-86 names this explicitly
 * and it is the difference between a projection and a reassurance.
 */
export const BURN_RATE_WINDOW_MONTHS = 3;
/**
 * Days per month used to turn a monthly burn rate into a runway in days.
 *
 * Thirty, and stated rather than hidden, so the arithmetic on the screen can be
 * checked by hand: balance ÷ monthly burn × 30 = days. A calendar-exact figure
 * would be more precise about a number whose inputs are a three-month average —
 * precision the projection does not have and should not imply.
 */
export const BURN_RATE_DAYS_PER_MONTH = 30;
/**
 * Whether a runway could be projected at all, and why not when it could not.
 *
 * The two failure states are separate because they mean different things to the
 * person reading the row. `no_burn` is a matter nothing is being spent on —
 * dormant, maybe closed, and not a funding problem. `insufficient_data` is a
 * matter we cannot say anything about, which for a new matter with a trial next
 * month is the row Finance most needs to look at.
 *
 * Neither is ever rendered as a runway. KAN-86: a zero burn rate must not show
 * as an infinite runway, and a new matter must not be given a fabricated one.
 */
export const TRUST_PROJECTION_STATUSES = ['projected', 'no_burn', 'insufficient_data'];
export const TRUST_PROJECTION_STATUS_META = {
    projected: {
        label: 'Projected',
        description: "Runway derived from this matter's own trust spending over the trailing three months.",
    },
    no_burn: {
        label: 'No projection',
        description: 'Nothing has left trust on this matter in the last three months. A dormant matter has no runway to project rather than an unlimited one.',
    },
    insufficient_data: {
        label: 'Not enough history',
        description: 'Too new to have a trailing burn rate. Stated as unknown rather than given a figure borrowed from other matters — the balance threshold and any upcoming hearing still apply.',
    },
};
/**
 * Runway in whole days at a measured burn rate.
 *
 * Returns null where a number would be a lie: no burn rate means no runway, and
 * that case is the caller's to report as `no_burn` rather than as zero or
 * infinity. A balance already at or below zero has no days left, which is a
 * real answer and not a missing one.
 *
 * Integer arithmetic throughout — this is minor units and a float here would
 * eventually put a matter in the wrong horizon.
 */
export function depletionDays(balanceMinor, monthlyBurnMinor) {
    if (monthlyBurnMinor <= 0)
        return null;
    if (balanceMinor <= 0)
        return 0;
    return Math.floor((balanceMinor * BURN_RATE_DAYS_PER_MONTH) / monthlyBurnMinor);
}
/* ------------------------------------------------------------------------ *
 * Upcoming hearings and trials (KAN-86)
 * ------------------------------------------------------------------------ */
/**
 * What kind of court event a calendar entry is.
 *
 * Clio's calendar carries no structured event type that every tenant populates,
 * so this is read from the entry's summary. That is keyword matching, which
 * this codebase refuses for classifying *money* — a refund's reason is left
 * `unclassified` rather than guessed at. The difference is what the answer is
 * used for: a misread reason becomes a figure on a dashboard that reads as
 * fact, whereas a misread event only ever adds or omits a matter from a list
 * Finance is about to look at, with the entry's own title printed beside it.
 *
 * The rule is published in `MATTER_EVENT_RULE` and rendered on the screen, the
 * same discipline the AR risk rule and refund concentration follow: a threshold
 * nobody can see is a threshold nobody can argue with.
 */
export const MATTER_EVENT_KINDS = ['trial', 'hearing', 'other'];
export const MATTER_EVENT_KIND_META = {
    trial: {
        label: 'Trial',
        description: 'A trial date. The most expensive thing a matter does, and the least postponable.',
    },
    hearing: {
        label: 'Hearing',
        description: 'A court appearance, motion, deposition, mediation or conference — anything that draws on the retainer before it happens.',
    },
    other: {
        label: 'Other',
        description: 'A calendar entry that reads as neither. Kept and shown on the matter, but never used to flag a funding risk.',
    },
};
/**
 * Words that make a calendar entry a hearing, matched on whole words.
 *
 * Checked **before** the trial words, and that order is load-bearing: a
 * pre-trial conference is a hearing, and `\btrial\b` matches "pre-trial"
 * because the hyphen is a word boundary. Testing hearings first is what keeps
 * the cheaper event from being labelled the expensive one.
 */
export const HEARING_KEYWORDS = [
    'pretrial',
    'pre-trial',
    'hearing',
    'motion',
    'court',
    'deposition',
    'mediation',
    'arbitration',
    'conference',
    'settlement',
    'status',
];
export const TRIAL_KEYWORDS = ['trial'];
/** The rule, in the words the screen prints beside the flags it produces. */
export const MATTER_EVENT_RULE = 'A Clio calendar entry counts as a trial or hearing when its title contains one of a published list of words, matched whole. Hearing words are tested first, so a pre-trial conference is a hearing rather than a trial.';
function containsWord(haystack, word) {
    const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`).test(haystack);
}
/**
 * Which kind of event a calendar entry's title describes.
 *
 * An untitled entry is `other`, never a hearing: an empty string matches
 * nothing, and defaulting an unreadable entry into the flagging path would
 * put matters on the dashboard for a calendar row nobody can identify.
 */
export function classifyMatterEvent(summary) {
    const text = (summary ?? '').toLowerCase();
    if (text.trim() === '')
        return 'other';
    if (HEARING_KEYWORDS.some((word) => containsWord(text, word)))
        return 'hearing';
    if (TRIAL_KEYWORDS.some((word) => containsWord(text, word)))
        return 'trial';
    return 'other';
}
/** True for the kinds that draw on a retainer, and so can flag a funding risk. */
export function isFundableEvent(kind) {
    return kind === 'trial' || kind === 'hearing';
}
/**
 * Why no cost estimate is attached to an event, stated where it will be read.
 *
 * KAN-86 says to ask Finance for expected retainer amounts per event type and,
 * failing that, to flag on balance alone rather than inventing an estimate.
 * Nobody has been asked yet, so nothing is invented: an event flags a matter
 * when the balance is already under the funding floor, and the message below is
 * rendered on the screen so the limitation is the reader's rather than a
 * surprise later.
 */
export const EVENT_COST_ESTIMATE_NOTE = 'No expected cost is attached to a hearing or trial: the firm has not set per-event retainer amounts, and an invented figure would decide which matters get flagged. An event therefore flags a matter only when its balance is already under the funding floor, with the date shown so the lead time is visible.';
/**
 * The firm's starting rule, and every number in it is a default awaiting
 * Finance rather than a policy — the same standing as `DEFAULT_AR_RISK_RULE`.
 *
 *  - **$2,500** because it is roughly a single appearance's worth of work at
 *    the firm's rates: below it, a matter cannot absorb one ordinary week.
 *  - **One month of burn** because a matter spending $8,000 a month is not
 *    comfortable at $2,600, and the absolute floor alone would say it was.
 *  - **30 days** on the runway, which KAN-86 suggests, and which is about the
 *    lead time a retainer top-up actually needs from a client.
 *  - **30 days** on hearings for the same reason: the ticket's own test is that
 *    catching an underfunded matter two weeks before a trial is useful and the
 *    day before is not.
 *
 * KAN-86's definition of done is that Finance names the matters it already
 * knows to be underfunded and this rule surfaces them. If it does not, these
 * four numbers are what is wrong.
 */
export const DEFAULT_TRUST_FUNDING_RULE = {
    minBalanceMinor: 250_000,
    minBurnMonths: 1,
    depletionHorizonDays: 30,
    eventHorizonDays: 30,
};
/**
 * The floor this matter is measured against, given its own burn rate.
 *
 * Returned rather than applied inline so the screen can print it: "under
 * $8,000" is checkable, "flagged" is not.
 */
export function fundingFloorMinor(monthlyBurnMinor, rule) {
    const burnFloor = Math.max(0, monthlyBurnMinor) * Math.max(0, rule.minBurnMonths);
    return Math.max(rule.minBalanceMinor, burnFloor);
}
/* ------------------------------------------------------------------------ *
 * Risk signals and levels (KAN-86)
 * ------------------------------------------------------------------------ */
/**
 * Why a matter is flagged. A matter can trip several, and all of them are
 * reported — the reason is the actionable part, and collapsing four causes into
 * one severity word is how a dashboard becomes a list nobody trusts.
 */
export const TRUST_RISK_SIGNALS = [
    'overdrawn',
    'below_threshold',
    'projected_depletion',
    'event_unfunded',
];
export const TRUST_RISK_SIGNAL_META = {
    overdrawn: {
        label: 'Negative balance',
        description: 'More has left trust on this matter than went in. Either a real overdraw, which is a trust-account breach, or a sync fault. Both need looking at today.',
    },
    below_threshold: {
        label: 'Below the floor',
        description: "Balance is under the larger of the absolute floor and the matter's own monthly burn multiple.",
    },
    projected_depletion: {
        label: 'Running out',
        description: 'At the trailing three-month burn rate, this balance reaches zero inside the horizon.',
    },
    event_unfunded: {
        label: 'Hearing unfunded',
        description: 'A hearing or trial is coming up and the balance is already under the floor. Flagged on the balance alone — no expected cost is assumed for the event.',
    },
};
/**
 * Risk levels, worst last so the tuple reads as an ordering.
 *
 * Three, not four, and there is no `watch` tier of the kind AR has. A second
 * threshold below the first would be a number nobody chose, and this feature
 * already refuses to invent those. A matter is overdrawn, or it trips a rule
 * Finance set, or it does not.
 */
export const TRUST_RISK_LEVELS = ['none', 'at_risk', 'overdrawn'];
export const TRUST_RISK_META = {
    overdrawn: {
        label: 'Overdrawn',
        description: 'Negative trust balance. Reported separately from every other risk because it is an alarm rather than a degree of one.',
    },
    at_risk: {
        label: 'At risk',
        description: 'Trips at least one of the rules below without being overdrawn.',
    },
    none: {
        label: 'Funded',
        description: 'Above the floor, with no projected depletion or unfunded hearing in the horizon.',
    },
};
/**
 * Applies the rule to one matter.
 *
 * `below_threshold` is not raised for an overdrawn matter. A negative balance
 * is obviously under any floor, and reporting both would pad the reason list
 * on precisely the rows whose one real reason needs to stand alone.
 */
export function assessTrustRisk(input, rule) {
    const floorMinor = fundingFloorMinor(input.burn.monthlyBurnMinor, rule);
    const signals = [];
    const overdrawn = input.balanceMinor < 0;
    if (overdrawn)
        signals.push('overdrawn');
    else if (input.balanceMinor < floorMinor)
        signals.push('below_threshold');
    /**
     * "Running out" is only news while there is something left. A matter already
     * at or below zero has run out, which `overdrawn` or `below_threshold`
     * has just said — and reporting both puts a second reason on the row whose
     * one real reason most needs to stand alone.
     */
    if (input.balanceMinor > 0 &&
        input.burn.status === 'projected' &&
        input.burn.daysRemaining !== null &&
        input.burn.daysRemaining <= rule.depletionHorizonDays) {
        signals.push('projected_depletion');
    }
    /**
     * The event signal needs both halves. An upcoming trial on a fully funded
     * matter is not a funding problem, and flagging it would fill the dashboard
     * with the firm's court calendar.
     */
    if (input.daysToNextEvent !== null &&
        input.daysToNextEvent <= rule.eventHorizonDays &&
        input.balanceMinor < floorMinor) {
        signals.push('event_unfunded');
    }
    const level = overdrawn ? 'overdrawn' : signals.length > 0 ? 'at_risk' : 'none';
    return { level, signals, floorMinor };
}
/** How the dashboard is ordered. Each is an ordering, never a weighted score. */
export const TRUST_SORTS = ['risk', 'balance', 'runway', 'event'];
export const TRUST_SORT_META = {
    risk: {
        label: 'Risk',
        description: 'Overdrawn first, then by how many rules the matter trips, then by the shortest runway. No weighted score is computed.',
    },
    balance: { label: 'Balance', description: 'Lowest trust balance first, negatives at the top.' },
    runway: {
        label: 'Runway',
        description: 'Shortest projected runway first. Matters with no projection sort last rather than as zero.',
    },
    event: {
        label: 'Next hearing',
        description: 'Soonest upcoming hearing or trial first. Matters with none sort last.',
    },
};
//# sourceMappingURL=trust.js.map